// CARO. Casa de estilo — typed client for the Worker API.

import type {
  ApiResult,
  Article,
  InstagramPost,
  OrderStatusView,
  Product,
  ProductCategory,
  PublicPack,
  Testimonial,
} from '@/types';
import { getCartId } from '@/client/lib/cartId';
import type { PublicLetter, PublicLetterSummary } from '@/lib/letters';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { accept: 'application/json' } });
  const json = (await res.json()) as ApiResult<T>;
  if (!json.ok) throw new ApiClientError(json.error.error, json.error.code);
  return json.data;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as ApiResult<T>;
  if (!json.ok) {
    throw new ApiClientError(json.error.error, json.error.code, json.error.details);
  }
  return json.data;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * A transmit-step soldOut / backorder entry. `name` is the ES base product name;
 * `size` is set for one size of a sized product (the client adds the localized
 * size label). No `size` = the whole product (every line of it). `packId` +
 * `packName` (ES) = the product is a component of that pack line: the entry
 * concerns the pack line, not a loose line of the product.
 */
export interface StockEntry {
  productId: string;
  name: string;
  size?: string;
  packId?: string;
  packName?: string;
}

/**
 * A line issue from the quote (`priceCart` issues) or from a `cart_invalid`
 * checkout error (`details.issues`). `code` is a `CartIssueCode`; the client
 * acts on `size_required` (a line stored without size for a now-sized product).
 */
export interface QuoteIssue {
  /** '' for an issue of a whole pack (unknown / not active pack). */
  productId: string;
  size?: string;
  /** Set when the issue concerns (a component of) a pack line. */
  packId?: string;
  code: string;
}

/**
 * Product ids with a blocking `size_required` issue in an issues payload (any
 * shape). Loose lines only: a pack component's issue (`packId`) concerns the
 * pack line, not a loose line of that product.
 */
export function sizeRequiredIds(issues: unknown): string[] {
  if (!Array.isArray(issues)) return [];
  return issues.flatMap((i: unknown) => {
    const it = i as Partial<QuoteIssue> | null;
    return it && it.code === 'size_required' && typeof it.productId === 'string' && !it.packId
      ? [it.productId]
      : [];
  });
}

export interface CheckoutResponse {
  /** Present on the normal payment path. */
  orderNumber?: string;
  mode?: 'widget' | 'hosted';
  hostedUrl?: string | null;
  checkoutId?: string | null;
  /** Present when the validator suggests a corrected address. */
  addressSuggestion?: import('@/types').SuggestedAddress | null;
  /** Present when the address wasn't found at all. */
  addressUnverified?: boolean;
  /** Present when the address couldn't be verified → manual resolution by email. */
  manualEnquiry?: boolean;
  reference?: string;
  totals?: import('@/types').OrderTotals;
  totalFormatted?: string;
  /** Transmit-step availability: pieces that just sold out (non pre-order). */
  soldOut?: StockEntry[];
  /** Short pre-order (reasortimiento) lines: warn, then continue or remove. */
  backorder?: StockEntry[];
}

export const api = {
  products: (params?: { category?: string; featured?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    if (params?.featured) q.set('featured', 'true');
    q.set('cartId', getCartId());
    return get<Product[]>(`/api/products?${q.toString()}`);
  },
  product: (slug: string) =>
    get<Product>(`/api/products/${slug}?cartId=${encodeURIComponent(getCartId())}`),
  /** Available packs (of one store), server-priced; shown before the products. */
  packs: (params?: { category?: ProductCategory }) => {
    const q = new URLSearchParams();
    if (params?.category) q.set('category', params.category);
    q.set('cartId', getCartId());
    return get<PublicPack[]>(`/api/packs?${q.toString()}`);
  },
  /** A pack page: 404 when missing/draft/archived; `available` may be false. */
  pack: (slug: string) =>
    get<PublicPack>(
      `/api/packs/${encodeURIComponent(slug)}?cartId=${encodeURIComponent(getCartId())}`,
    ),
  /** La carta: the published letters (the /newsletter archive), newest first. */
  letters: () => get<PublicLetterSummary[]>('/api/letters'),
  /** One published letter (the slug of any language); 404 for drafts. */
  letter: (year: string, month: string, slug: string) =>
    get<PublicLetter>(`/api/letters/${encodeURIComponent(year)}/${encodeURIComponent(month)}/${encodeURIComponent(slug)}`),
  /** How many finds are public now (the «Hallazgos» entry shows only when > 0). */
  findsSummary: () => get<{ live: number }>('/api/finds/summary'),
  testimonials: () => get<Testimonial[]>('/api/testimonials'),
  articles: (category?: string) =>
    get<Article[]>(`/api/articles${category ? `?category=${category}` : ''}`),
  article: (slug: string) => get<Article>(`/api/articles/${slug}`),
  /** Published articles that link a product (its «En el Edit» block). */
  articlesForProduct: (slug: string) =>
    get<Article[]>(`/api/articles?product=${encodeURIComponent(slug)}`),
  instagram: () => get<InstagramPost[]>('/api/instagram'),
  orderStatus: (ref: string) =>
    get<OrderStatusView>(`/api/orders/${ref}/status`),
  checkout: (body: unknown) => post<CheckoutResponse>('/api/checkout', body),
  quote: (body: {
    items: import('@/types').CartRequestLine[];
    country: string;
  }) =>
    post<{
      totals: import('@/types').OrderTotals;
      shippingKnown: boolean;
      ok?: boolean;
      issues?: QuoteIssue[];
    }>(
      '/api/checkout/quote',
      { ...body, cartId: getCartId() },
    ),
  validateAddress: (body: {
    address1: string;
    city: string;
    postalCode: string;
    country: string;
  }) => post<{ suggestions: import('@/types').SuggestedAddress[] }>('/api/address/check', body),
  verifyCheckout: (orderNumber: string) =>
    post<{ orderNumber: string; paymentStatus: string }>(
      `/api/checkout/${orderNumber}/verify`,
      {},
    ),
  contact: (body: unknown) => post<{ received: boolean }>('/api/contact', body),
  newsletter: (body: unknown) =>
    post<{ subscribed: boolean }>('/api/newsletter', body),
  enquiry: (body: unknown) => post<{ received: boolean }>('/api/enquiries', body),
  restockAlert: (body: unknown) => post<{ received: boolean }>('/api/restock-alerts', body),
};
